import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CloudUpload, Trash2 } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { XCircle, CheckCircle } from "lucide-react"; // Lucide ikonok

const AddProductModal = ({
  isAdding,
  setIsAdding,
  newProduct,
  setNewProduct,
  handleAddProduct,
  previewImage,
  setPreviewImage,
}) => {
  const [successMessage, setSuccessMessage] = useState("");
  const { darkMode } = useTheme();

  if (!isAdding) return null;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewProduct({ ...newProduct, imageFile: file });
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setNewProduct({ ...newProduct, imageFile: null });
    setPreviewImage(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const decimalRegex = /^-?\d+(\.\d{1,30})?$/;
    if (
      !newProduct.name.trim() ||
      !decimalRegex.test(newProduct.price) ||
      !decimalRegex.test(newProduct.priceMarmurist)
    )
      return;

    handleAddProduct();
    setSuccessMessage("✅ Produsul a fost adăugat cu succes!");

    setTimeout(() => {
      setSuccessMessage("");
      setIsAdding(false);
      setPreviewImage(null);
    }, 2000);
  };

  return (
    <>
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="fixed top-4 left-[42%] transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg text-center z-50 flex items-center gap-2"
          >
            {successMessage}
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={`fixed inset-0 flex items-center justify-center bg-opacity-80 ${darkMode ? "bg-black" : "bg-gray-500"
          }`}
        onClick={(e) => e.target === e.currentTarget && setIsAdding(false)}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className={`p-6 rounded-3xl shadow-xl w-[600px] relative ${darkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ❌ Bezárás gomb */}
          <button
            onClick={() => setIsAdding(false)}
            className="absolute top-2 right-3 text-gray-400 hover:text-red-500 transition text-2xl"
          >
            ✖
          </button>

          <h3
            className={`text-2xl font-semibold mb-4 text-center ${darkMode ? "text-blue-400" : "text-blue-600"
              }`}
          >
            ➕ Adaugă un produs
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 📌 Termék neve */}
            <label className="block font-semibold">Numele produsului:</label>
            <input
              type="text"
              placeholder="Ex: Granit Black"
              value={newProduct.name}
              onChange={(e) =>
                setNewProduct({ ...newProduct, name: e.target.value })
              }
              className={`w-full p-2 border rounded-3xl ${darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-black"
                }`}
              required
            />

            {/* 🔥 ÚJ: Mindkét ár megadása */}
            <label className="block font-semibold">
              Preț pentru Client Final (EUR):
            </label>
            <input
              type="text"
              placeholder="Ex: 150"
              value={newProduct.price}
              onChange={(e) =>
                setNewProduct({
                  ...newProduct,
                  price: e.target.value,
                })
              }
              className={`w-full p-2 border rounded-3xl ${darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-black"
                }`}
              min="0"
              required
            />

            <label className="block font-semibold">
              Preț pentru Client Marmist (EUR):
            </label>
            <input
              type="text"
              placeholder="Ex: 130"
              value={newProduct.priceMarmurist}
              onChange={(e) =>
                setNewProduct({
                  ...newProduct,
                  priceMarmurist: e.target.value,
                })
              }
              className={`w-full p-2 border rounded-3xl ${darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-black"
                }`}
              min="0"
              required
            />

            <label className="block font-semibold">Preț de achiziție (EUR):</label>
            <input
              type="text"
              placeholder="Ex: 100"
              value={newProduct.costPrice}
              onChange={(e) =>
                setNewProduct({
                  ...newProduct,
                  costPrice: e.target.value,
                })
              }
              className={`w-full p-2 border rounded-3xl ${darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-black"
                }`}
              min="0"
              required
            />

            {/* 📌 Készlet */}
            <label className="block font-semibold">Stoc disponibil:</label>
            <input
              type="number"
              placeholder="Ex: 50"
              value={newProduct.availableStock}
              onChange={(e) =>
                setNewProduct({ ...newProduct, availableStock: e.target.value })
              }
              className={`w-full p-2 border rounded-3xl ${darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-black"
                }`}
              min="0"
              required
            />

            {/* 📌 Mértékegység választó */}
            <label className="block font-semibold">Unitate de măsură:</label>
            <select
              value={newProduct.unitType}
              onChange={(e) =>
                setNewProduct({ ...newProduct, unitType: e.target.value })
              }
              className={`w-full p-2 border rounded-3xl ${darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-black"
                }`}
            >
              <option value="MP">MP</option>
              <option value="BUC">BUC</option>
            </select>

            {/* 📌 Kép feltöltés */}
            <label className="block font-semibold">Imagine:</label>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="fileInput"
              />
              <label
                htmlFor="fileInput"
                className={`flex items-center justify-center gap-2 py-4 px-4 rounded-3xl cursor-pointer transition ${darkMode
                    ? "bg-blue-700 hover:bg-blue-600 text-white"
                    : "bg-blue-600 hover:bg-blue-500 text-white"
                  }`}
              >
                <CloudUpload size={25} />
                <span className="text-xl font-semibold">
                  {newProduct.imageFile ? "Schimbă imaginea" : "Alege fișier"}
                </span>{" "}
              </label>
            </div>

            {/* 🔥 Kép előnézet */}
            {previewImage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="relative mt-4"
              >
                <img
                  src={previewImage}
                  alt="Previzualizare"
                  className="w-full h-40 object-cover rounded shadow-md"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className={`absolute top-2 right-2 p-1 rounded-full transition ${darkMode
                      ? "bg-red-700 hover:bg-red-600 text-white"
                      : "bg-red-600 hover:bg-red-500 text-white"
                    }`}
                >
                  <Trash2 size={18} />
                </button>
              </motion.div>
            )}

            <div className="flex justify-between mt-4 gap-4">
              <button
                onClick={() => {
                  setIsAdding(false);
                  setPreviewImage(null);
                }}
                className={`w-full px-5 py-5 rounded-3xl transition flex items-center justify-center gap-2 text-lg font-semibold ${darkMode
                    ? "bg-gray-600 text-white hover:bg-gray-500"
                    : "bg-gray-500 text-white hover:bg-gray-400"
                  }`}
              >
                <XCircle size={24} /> Anulare
              </button>

              <button
                type="submit"
                className={`w-full px-5 py-5 rounded-3xl transition flex items-center justify-center gap-2 text-lg font-semibold ${darkMode
                    ? "bg-green-700 text-white hover:bg-green-600"
                    : "bg-green-600 text-white hover:bg-green-500"
                  }`}
              >
                <CheckCircle size={24} /> Adaugă
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </>
  );
};

export default AddProductModal;
